// Vehículos de la obra: cada vehículo que se vio, seguido entre fotos y entre días.
// (Viene de la página Vehículos, rama feature/vehiculos; acá se usa con las clases del modelo nuevo iaV3 para
// Inteligencia › Entradas y salidas. Acepta también los nombres de clase del YOLOX.)
// Todo se calcula en el navegador con las mismas detecciones que ya carga el Dashboard IA (sin costo extra de IA).
//
// 1) En un día: misma clase + caja que se superpone (o centro cerca) en fotos seguidas, tolerando hasta 22 min
//    sin verlo (misma regla de visitas que el Calendario de obra, entregas.js) = un "tramo".
// 2) Color: se recorta la parte central de la caja en la mejor foto del tramo, se achica en un canvas y cada
//    píxel vota un color con nombre (blanco, gris, negro, rojo, ...). Se guarda en IndexedDB.
// 3) Entre días (y entre tramos del mismo día): mismo tipo + mismo color + lugar parecido + que no estén
//    al mismo tiempo = el mismo vehículo.
// 4) Movimiento: cuánto se corrió el centro de la caja durante el día (en % del ancho de la foto).
import { dayKey, arHour, photoUrl } from '../iaData';
import { iou, loadImg, DELIV } from '../entregas';

export const VEH = {
  minConf: DELIV.minConf, // confianza mínima de la caja
  maxGapMin: DELIV.maxGapMin, // minutos sin verlo que se toleran dentro de un tramo
  iou: DELIV.iou, // superposición para seguir al mismo vehículo entre fotos seguidas
  near: 0.6, // o centro a menos de 0,6 anchos de caja
  soloConf: 0.7, // un vehículo visto en una sola foto cuenta sólo si la caja es muy clara
  moveThr: 0.05, // se corrió más del 5% del ancho de la foto = se movió
  spotThr: 0.08, // mismo lugar entre días: centros a menos del 8% del ancho de la foto
  hourFrom: 6, hourTo: 20, // de noche las fotos no sirven para colores ni para seguir vehículos
  maxCrops: 160, // recortes de color como máximo por consulta
};

// Grupo para seguir (el modelo duda entre camión y volquete, y entre excavadora y retro)
const GROUP = {
  Automovil: 'auto', Camioneta: 'camioneta', Camion: 'camion', 'Camion volquete': 'camion', Mixer: 'mixer', Excavadora: 'excavadora', Retroexcavadora: 'excavadora',
  // iaV3 (RF-DETR)
  auto: 'auto', camioneta: 'camioneta', camion: 'camion', volquete: 'camion', mixer: 'mixer', bomba_hormigon: 'bomba',
  excavadora: 'excavadora', retroexcavadora: 'excavadora', minicargadora: 'minicargadora', grua_movil: 'grua', autoelevador: 'autoelevador',
};
export const TYPES = [
  { key: 'auto', label: 'Auto', color: '#3b82f6' },
  { key: 'camioneta', label: 'Camioneta', color: '#6366f1' },
  { key: 'camion', label: 'Camión', color: '#c2410c' },
  { key: 'volquete', label: 'Camión volquete', color: '#f97316' },
  { key: 'mixer', label: 'Mixer', color: '#a855f7' },
  { key: 'bomba', label: 'Bomba de hormigón', color: '#7c3aed' },
  { key: 'excavadora', label: 'Excavadora', color: '#f59e0b' },
  { key: 'retro', label: 'Retroexcavadora', color: '#d97706' },
  { key: 'minicargadora', label: 'Minicargadora', color: '#ca8a04' },
  { key: 'grua', label: 'Grúa móvil', color: '#b45309' },
  { key: 'autoelevador', label: 'Autoelevador', color: '#92400e' },
];
export const typeInfo = (k) => TYPES.find((t) => t.key === k) || { key: k, label: k, color: '#6b7280' };

export const COLORS = [
  { key: 'blanco', label: 'blanco', fem: 'blanca', hex: '#f8fafc' },
  { key: 'gris', label: 'gris', fem: 'gris', hex: '#9ca3af' },
  { key: 'negro', label: 'negro', fem: 'negra', hex: '#111827' },
  { key: 'rojo', label: 'rojo', fem: 'roja', hex: '#dc2626' },
  { key: 'azul', label: 'azul', fem: 'azul', hex: '#2563eb' },
  { key: 'amarillo', label: 'amarillo', fem: 'amarilla', hex: '#facc15' },
  { key: 'verde', label: 'verde', fem: 'verde', hex: '#16a34a' },
  { key: 'naranja', label: 'naranja', fem: 'naranja', hex: '#f97316' },
  { key: 'marron', label: 'marrón / beige', fem: 'marrón / beige', hex: '#a16207' },
];
export const colorInfo = (k) => COLORS.find((c) => c.key === k) || null;
const FEM = new Set(['camioneta', 'excavadora', 'retro', 'minicargadora', 'grua', 'bomba']);
export const vehicleName = (v) => {
  const t = typeInfo(v.type).label;
  const c = colorInfo(v.color);
  if (!c) return t;
  return `${t} ${FEM.has(v.type) ? c.fem : c.label}`;
};

// Caja cortada por el borde de la foto: se ve sólo una parte del vehículo (peor para el color y la miniatura)
const atEdge = (o) => o.d.x1 <= 3 || o.d.y1 <= 3 || o.d.x2 >= (o.p.w || 1920) - 3 || o.d.y2 >= (o.p.h || 1080) - 3;
const quality = (o) => o.d.conf * Math.sqrt(o.d.x2 - o.d.x1) * (atEdge(o) ? 0.3 : 1);
const cx = (b) => (b.x1 + b.x2) / 2, cy = (b) => (b.y1 + b.y2) / 2, bw = (b) => b.x2 - b.x1;

// ---------------------------------------------------------------------------
// 1) Tramos de un día
// ---------------------------------------------------------------------------
function vehicleDets(p) {
  const ds = p.dets.filter((d) => GROUP[d.cls] && d.x1 != null && d.conf >= VEH.minConf).sort((a, b) => b.conf - a.conf);
  // Una misma cosa con dos cajas (camión y volquete a la vez): queda la más clara
  const out = [];
  for (const d of ds) if (!out.some((o) => iou(o, d) > 0.6)) out.push(d);
  return out;
}

function dayTracks(photos) {
  const tracks = [];
  // Se tolera UNA foto sin verlo (el modelo no lo vio o algo lo tapó): con fotos cada 20 min son ~44 min
  const gaps = photos.slice(1).map((p, i) => p.ms - photos[i].ms).sort((a, b) => a - b);
  const maxGap = Math.max(VEH.maxGapMin * 60e3, gaps.length ? 2.2 * gaps[Math.floor(gaps.length / 2)] : 0);
  let open = [];
  photos.forEach((p, i) => {
    const dets = vehicleDets(p);
    const pairs = [];
    dets.forEach((d, di) => open.forEach((t, ti) => {
      if (t.group !== GROUP[d.cls]) return;
      const l = t.last;
      const v = iou(d, l);
      const dist = Math.hypot(cx(d) - cx(l), cy(d) - cy(l)) / Math.max(bw(d), bw(l), 1);
      const s = v > VEH.iou ? 1 + v : dist < VEH.near ? 1 - dist : 0;
      if (s > 0) pairs.push([s, di, ti]);
    }));
    pairs.sort((a, b) => b[0] - a[0]);
    const usedD = new Set(), usedT = new Set();
    for (const [, di, ti] of pairs) {
      if (usedD.has(di) || usedT.has(ti)) continue;
      usedD.add(di); usedT.add(ti);
      open[ti].obs.push({ p, d: dets[di] }); open[ti].last = dets[di];
    }
    dets.forEach((d, di) => {
      if (usedD.has(di)) return;
      const t = { group: GROUP[d.cls], obs: [{ p, d }], last: d };
      tracks.push(t); open.push(t);
    });
    const next = photos[i + 1];
    open = next ? open.filter((t) => next.ms - t.obs[t.obs.length - 1].p.ms <= maxGap) : [];
  });
  return tracks
    .filter((t) => t.obs.length >= 2 || t.obs[0].d.conf >= VEH.soloConf)
    .map((t) => {
      const o0 = t.obs[0], oN = t.obs[t.obs.length - 1];
      const W = o0.p.w || 1920;
      let move = 0;
      for (const o of t.obs) move = Math.max(move, Math.hypot(cx(o.d) - cx(o0.d), cy(o.d) - cy(o0.d)) / W);
      const votes = {};
      t.obs.forEach((o) => { votes[o.d.cls] = (votes[o.d.cls] || 0) + o.d.conf; });
      const best = t.obs.reduce((a, o) => (quality(o) > quality(a) ? o : a));
      return {
        key: `${o0.p.id}|${Math.round(o0.d.x1)},${Math.round(o0.d.y1)}`,
        day: dayKey(o0.p.ms),
        group: t.group,
        votes,
        first: o0.p.ms, last: oN.p.ms,
        obs: t.obs,
        best,
        W,
        start: { x: cx(o0.d) / W, y: cy(o0.d) / W },
        end: { x: cx(oN.d) / W, y: cy(oN.d) / W },
        move,
        moved: move > VEH.moveThr,
      };
    });
}

// Todas las fotos del período (ordenadas) -> tramos
export function buildTracks(photos) {
  const days = new Map();
  for (const p of photos) {
    if (!p.hasBoxes) continue;
    const h = arHour(p.ms);
    if (h < VEH.hourFrom || h >= VEH.hourTo) continue;
    const k = dayKey(p.ms);
    if (!days.has(k)) days.set(k, []);
    days.get(k).push(p);
  }
  const out = [];
  for (const ps of days.values()) out.push(...dayTracks(ps));
  return out.sort((a, b) => a.first - b.first);
}

// Días con fotos (la cámara andaba) en el período
export function daysWithPhotos(photos) {
  const s = new Set();
  for (const p of photos) { const h = arHour(p.ms); if (h >= VEH.hourFrom && h < VEH.hourTo) s.add(dayKey(p.ms)); }
  return s;
}

// ---------------------------------------------------------------------------
// 2) Color dominante del recorte (con caché en IndexedDB)
// ---------------------------------------------------------------------------
const IDB_NAME = 'lisual-vehiculos', IDB_STORE = 'color4';
let idbP = null;
const idb = () => {
  if (!idbP) {
    idbP = new Promise((ok) => {
      try {
        const r = indexedDB.open(IDB_NAME, 4);
        r.onupgradeneeded = () => { if (!r.result.objectStoreNames.contains(IDB_STORE)) r.result.createObjectStore(IDB_STORE); };
        r.onsuccess = () => ok(r.result);
        r.onerror = () => ok(null);
      } catch (e) { ok(null); }
    });
  }
  return idbP;
};
const idbGet = async (k) => {
  const db = await idb();
  if (!db) return undefined;
  return new Promise((ok) => {
    try { const r = db.transaction(IDB_STORE).objectStore(IDB_STORE).get(k); r.onsuccess = () => ok(r.result); r.onerror = () => ok(undefined); } catch (e) { ok(undefined); }
  });
};
const idbPut = async (k, v) => {
  const db = await idb();
  if (!db) return;
  try { db.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).put(v, k); } catch (e) { /* sin espacio */ }
};

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const d = mx - mn;
  const s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  let h;
  if (mx === r) h = (g - b) / d + (g < b ? 6 : 0); else if (mx === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
  return [h * 60, s, l];
}
export function namedColor(r, g, b) {
  const [h, s, l] = rgbToHsl(r, g, b);
  if (l > 0.86) return 'blanco';
  if (l < 0.16) return 'negro';
  if (s < 0.22 || (s < 0.32 && l > 0.6)) return l > 0.64 ? 'blanco' : l < 0.3 ? 'negro' : 'gris';
  if (h < 15 || h >= 330) return l < 0.28 ? 'marron' : 'rojo';
  if (h < 45) return l < 0.42 || s < 0.45 ? 'marron' : 'naranja';
  if (h < 70) return s < 0.4 && l > 0.45 ? 'marron' : 'amarillo';
  if (h < 170) return 'verde';
  if (h < 265) return 'azul';
  return 'rojo';
}
// Votos de color de la zona central de la caja (sin ruedas, sin techo/cielo)
function cropColor(im, photo, d, sceneP90 = 1) {
  const sx = im.naturalWidth / (photo.w || im.naturalWidth), sy = im.naturalHeight / (photo.h || im.naturalHeight);
  const w = d.x2 - d.x1, h = d.y2 - d.y1;
  const x = (d.x1 + w * 0.2) * sx, y = (d.y1 + h * 0.2) * sy, cw = w * 0.6 * sx, ch = h * 0.55 * sy;
  if (cw < 4 || ch < 3) return null;
  const W = 24, H = 16;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(im, x, y, cw, ch, 0, 0, W, H);
  const px = ctx.getImageData(0, 0, W, H).data; // tira error si la foto no deja leer píxeles (CORS)
  const votes = {};
  const lum = [];
  for (let i = 0; i < px.length; i += 4) {
    const k = namedColor(px[i], px[i + 1], px[i + 2]);
    votes[k] = (votes[k] || 0) + 1;
    lum.push((Math.max(px[i], px[i + 1], px[i + 2]) + Math.min(px[i], px[i + 1], px[i + 2])) / 510);
  }
  const total = W * H;
  // Vidrios, sombras y ruedas tiran a negro: el negro gana sólo si es mayoría clara
  if ((votes.negro || 0) / total < 0.5) delete votes.negro;
  const rest = Object.values(votes).reduce((a, b) => a + b, 0);
  const top = Object.keys(votes).sort((a, b) => votes[b] - votes[a])[0];
  if (!top || votes[top] / rest < 0.35) return null;
  if (['blanco', 'gris', 'negro'].includes(top)) {
    // Blanco, gris o negro: las sombras oscurecen un vehículo blanco, así que manda la parte más iluminada de la chapa
    lum.sort((a, b) => a - b);
    const p90 = lum[Math.floor(lum.length * 0.9)], p50 = lum[Math.floor(lum.length * 0.5)];
    // Con luz pareja (nublado, neblina) un vehículo blanco no llega a 0,66: se compara con lo más claro de la foto
    if (p90 > 0.66 || (p90 > 0.5 && p90 >= sceneP90 - 0.03)) return 'blanco';
    return p50 < 0.22 ? 'negro' : 'gris';
  }
  return top;
}

// Luminancia del 90% más claro de toda la foto (para saber qué tan blanco puede verse algo blanco ese momento)
function sceneLum(im) {
  const c = document.createElement('canvas');
  c.width = 64; c.height = 36;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(im, 0, 0, 64, 36);
  const px = ctx.getImageData(0, 0, 64, 36).data;
  const l = [];
  for (let i = 0; i < px.length; i += 4) l.push((Math.max(px[i], px[i + 1], px[i + 2]) + Math.min(px[i], px[i + 1], px[i + 2])) / 510);
  l.sort((a, b) => a - b);
  return l[Math.floor(l.length * 0.9)];
}

const colorKey = (o) => `${o.p.s3}|${Math.round(o.d.x1)},${Math.round(o.d.y1)},${Math.round(o.d.x2)}`;

// Calcula el color de los tramos (los más largos primero, hasta maxCrops). Cada foto se baja una sola vez.
// onUpdate(colors: Map<trackKey, color|null>, hechos, total) se llama a medida que avanza.
export async function computeColors(tracks, onUpdate, isAlive = () => true) {
  const colors = new Map();
  const todo = [...tracks].sort((a, b) => b.obs.length - a.obs.length).slice(0, VEH.maxCrops);
  let done = 0;
  const pending = [];
  // Primero la caché
  await Promise.all(todo.map(async (t) => {
    const hit = await idbGet(colorKey(t.best));
    if (hit !== undefined) { colors.set(t.key, hit); done += 1; } else pending.push(t);
  }));
  if (!isAlive()) return colors;
  onUpdate(new Map(colors), done, todo.length);
  const byPhoto = new Map();
  for (const t of pending) {
    const k = t.best.p.id;
    if (!byPhoto.has(k)) byPhoto.set(k, []);
    byPhoto.get(k).push(t);
  }
  const queue = [...byPhoto.values()];
  let lastPaint = 0, blocked = false;
  const worker = async () => {
    while (queue.length && isAlive()) {
      const ts = queue.shift();
      const photo = ts[0].best.p;
      let im = null;
      if (!blocked) { try { im = await loadImg(await photoUrl(photo)); } catch (e) { im = null; } }
      let scene = 1;
      if (im) { try { scene = sceneLum(im); } catch (e) { scene = 1; } }
      for (const t of ts) {
        let c = null;
        if (im) {
          try { c = cropColor(im, photo, t.best.d, scene); idbPut(colorKey(t.best), c); } catch (e) { blocked = true; c = null; }
        }
        colors.set(t.key, c);
        done += 1;
      }
      const now = Date.now();
      if (now - lastPaint > 1500) { lastPaint = now; onUpdate(new Map(colors), done, todo.length); }
    }
  };
  await Promise.all([worker(), worker(), worker()]);
  if (isAlive()) onUpdate(new Map(colors), todo.length, todo.length);
  return colors;
}

// ---------------------------------------------------------------------------
// 3) Vehículos: tramos del mismo tipo, mismo color, lugar parecido y que no se pisan en el tiempo
// ---------------------------------------------------------------------------
const spotDist = (a, b) => Math.min(
  Math.hypot(a.start.x - b.start.x, a.start.y - b.start.y), Math.hypot(a.start.x - b.end.x, a.start.y - b.end.y),
  Math.hypot(a.end.x - b.start.x, a.end.y - b.start.y), Math.hypot(a.end.x - b.end.x, a.end.y - b.end.y),
);

const NEAR_COLORS = new Set(['blanco|gris', 'gris|blanco', 'gris|negro', 'negro|gris']);

export function buildVehicles(tracks, colors) {
  const vehicles = [];
  for (const t of tracks) {
    const tc = colors.get(t.key) ?? null;
    let best = null, bestD = Infinity;
    for (const v of vehicles) {
      if (v.group !== t.group) continue;
      // Blanco/gris y gris/negro se confunden con la luz: se aceptan sólo si está en el mismo lugar casi exacto
      const near = v.color && tc && v.color !== tc && NEAR_COLORS.has(`${v.color}|${tc}`);
      if (v.color && tc && v.color !== tc && !near) continue;
      if (v.tracks.some((o) => o.day === t.day && t.first <= o.last && o.first <= t.last)) continue;
      const d = Math.min(...v.tracks.map((o) => spotDist(o, t)));
      // Sin color de alguno de los dos, se pide el lugar casi exacto
      const lim = v.color && tc && !near ? VEH.spotThr : VEH.spotThr / 2;
      if (d < lim && d < bestD) { best = v; bestD = d; }
    }
    if (best) {
      best.tracks.push(t);
      if (!best.color && tc) best.color = tc;
    } else {
      vehicles.push({ group: t.group, color: tc, tracks: [t] });
    }
  }
  return vehicles.map((v, i) => finishVehicle(v, i, colors));
}

function finishVehicle(v, i, colors) {
  // Color: el que más tramos votaron
  const cv = {};
  // (cada tramo pesa por fotos, y mucho menos si sólo se lo vio cortado por el borde)
  v.tracks.forEach((t) => { const c = colors.get(t.key); if (c) cv[c] = (cv[c] || 0) + Math.sqrt(t.obs.length) * (atEdge(t.best) ? 0.25 : 1); });
  const color = Object.keys(cv).sort((a, b) => cv[b] - cv[a])[0] || null;
  // Tipo: el más votado (camión / volquete; excavadora y retro van juntas)
  let type = v.group;
  if (v.group === 'camion') {
    let c = 0, q = 0;
    v.tracks.forEach((t) => { c += (t.votes.Camion || 0) + (t.votes.camion || 0); q += (t.votes['Camion volquete'] || 0) + (t.votes.volquete || 0); });
    type = q > c ? 'volquete' : 'camion';
  }
  if (v.group === 'excavadora') {
    let e = 0, r = 0;
    v.tracks.forEach((t) => { e += (t.votes.Excavadora || 0) + (t.votes.excavadora || 0); r += (t.votes.Retroexcavadora || 0) + (t.votes.retroexcavadora || 0); });
    type = r > e ? 'retro' : 'excavadora';
  }
  const days = new Map();
  for (const t of v.tracks) {
    if (!days.has(t.day)) days.set(t.day, { day: t.day, tracks: [], first: t.first, last: t.last, photos: 0 });
    const d = days.get(t.day);
    d.tracks.push(t); d.first = Math.min(d.first, t.first); d.last = Math.max(d.last, t.last); d.photos += t.obs.length;
  }
  for (const d of days.values()) {
    d.tracks.sort((a, b) => a.first - b.first);
    // Se movió: se corrió dentro de un tramo, o volvió a aparecer en otro lugar
    d.moved = d.tracks.some((t) => t.moved) || d.tracks.some((t, j) => j > 0 && spotDist(t, d.tracks[j - 1]) > VEH.moveThr);
    d.leftAndBack = d.tracks.length > 1;
  }
  const all = v.tracks.flatMap((t) => t.obs);
  const best = v.tracks.map((t) => t.best).reduce((a, o) => (quality(o) > quality(a) ? o : a));
  const last = Math.max(...v.tracks.map((t) => t.last));
  return {
    id: `${v.group}-${i}-${v.tracks[0].key}`,
    type,
    color,
    days: [...days.values()].sort((a, b) => (a.day < b.day ? -1 : 1)),
    nDays: days.size,
    photos: all.length,
    best,
    last,
    moved: [...days.values()].some((d) => d.moved),
    // Un "camión" o "mixer" que nunca se mueve y está en el mismo lugar 3 días o más casi siempre es otra cosa
    // (una pared, un obrador, una hormigonera fija) que el detector confunde: se oculta salvo que se pida verlo.
    fixed: ['camion', 'mixer'].includes(v.group) && days.size >= 3 && ![...days.values()].some((d) => d.moved),
  };
}

// ---------------------------------------------------------------------------
// Búsqueda: "camioneta blanca", "camión rojo", "mixer"
// ---------------------------------------------------------------------------
const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const TYPE_WORDS = [
  [/^volquete/, 'volquete'], [/^(mixer|hormigonera|motohormigonera)/, 'mixer'], [/^camioneta|^pick ?up|^utilitario/, 'camioneta'],
  [/^camion/, 'camion'], [/^(auto|coche|vehiculo particular)/, 'auto'], [/^(excavadora|retro|maquina|pala)/, 'excavadora'],
];
const COLOR_WORDS = [
  [/^blanc/, 'blanco'], [/^gris|^plateado|^plata/, 'gris'], [/^negr/, 'negro'], [/^roj/, 'rojo'], [/^azul|^celeste/, 'azul'],
  [/^amarill/, 'amarillo'], [/^verde/, 'verde'], [/^naranja/, 'naranja'], [/^marron|^beige|^marr/, 'marron'],
];
export function parseQuery(q) {
  const out = { type: null, color: null, rest: [] };
  for (const w of norm(q || '').split(/[\s,]+/).filter(Boolean)) {
    const t = TYPE_WORDS.find(([re]) => re.test(w));
    const c = COLOR_WORDS.find(([re]) => re.test(w));
    if (t && !out.type) out.type = t[1];
    else if (c && !out.color) out.color = c[1];
    else if (!['de', 'el', 'la', 'los', 'las', 'un', 'una'].includes(w)) out.rest.push(w);
  }
  return out;
}
// "camión" encuentra camiones y volquetes; "volquete" sólo volquetes
export const typeMatches = (want, type) => !want || want === type || (want === 'camion' && type === 'volquete');
